import type { GameLogicCharacter } from "@types"
import React, { useCallback, useMemo, useRef, useState } from "react"
import * as Sentry from "@sentry/react"

type InputAvatar = GameLogicCharacter["avatar"]
type AvatarUpdate = InputAvatar | ((prev: InputAvatar) => InputAvatar)

type EditedAvatarActions = {
  setEditedAvatar: (avatar: AvatarUpdate) => void
  /** The current draft, without subscribing to it (for save handlers) */
  getEditedAvatar: () => InputAvatar
}

// Two contexts: the draft value (for the preview and the control rows) and
// stable actions (for the screens that only save it), so an arrow tap does
// not re-render the whole creation screen or the full-size editor modal.
const EditedAvatarValueContext = React.createContext<InputAvatar | undefined>(
  undefined,
)
const EditedAvatarActionsContext = React.createContext<
  EditedAvatarActions | undefined
>(undefined)

// Error fallback component
const ErrorFallback = ({ componentName }: { componentName: string }) => (
  <div
    style={{
      padding: "1rem",
      backgroundColor: "#FFF5F5",
      color: "#E53E3E",
      borderRadius: "0.375rem",
    }}
  >
    <p style={{ fontWeight: "bold" }}>
      Something went wrong in {componentName}
    </p>
    <p style={{ fontSize: "0.875rem" }}>
      We've been notified and are working on a fix
    </p>
  </div>
)

const missingProvider = () =>
  new Error("useEditedAvatar must be used within an EditedAvatarProvider")

/** The draft avatar plus its actions (re-renders on every draft change). */
export const useEditedAvatar = () => {
  const editedAvatar = React.useContext(EditedAvatarValueContext)
  const actions = React.useContext(EditedAvatarActionsContext)
  if (editedAvatar === undefined || actions === undefined) {
    throw missingProvider()
  }
  return { editedAvatar, ...actions }
}

/** Only the stable actions: never re-renders the caller on draft changes. */
export const useEditedAvatarActions = () => {
  const actions = React.useContext(EditedAvatarActionsContext)
  if (actions === undefined) throw missingProvider()
  return actions
}

export const EditedAvatarProvider: React.FC<{
  data: InputAvatar
  children: React.ReactNode
}> = ({ data, children }) => {
  const [editedAvatar, setEditedAvatarState] = useState<InputAvatar>(data)
  const latest = useRef(editedAvatar)
  latest.current = editedAvatar

  const setEditedAvatar = useCallback((update: AvatarUpdate) => {
    setEditedAvatarState(prev => {
      const next = typeof update === "function" ? update(prev) : update
      latest.current = next
      return next
    })
  }, [])
  const getEditedAvatar = useCallback(() => latest.current, [])
  const actions = useMemo(
    () => ({ setEditedAvatar, getEditedAvatar }),
    [setEditedAvatar, getEditedAvatar],
  )

  return (
    <Sentry.ErrorBoundary
      fallback={<ErrorFallback componentName="Avatar Editor Context" />}
    >
      <EditedAvatarActionsContext.Provider value={actions}>
        <EditedAvatarValueContext.Provider value={editedAvatar}>
          {children}
        </EditedAvatarValueContext.Provider>
      </EditedAvatarActionsContext.Provider>
    </Sentry.ErrorBoundary>
  )
}
